import type { QueryClient } from '@tanstack/react-query';
import type { AuthContextType } from '@/contexts/AuthContextDefinition';
import { debugStore } from './debugStore';

const SENSITIVE_KEYS = /password|token|secret|cvv|ssn/i;

function sanitize(data: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (SENSITIVE_KEYS.test(k)) {
      result[k] = '[REDACTED]';
    } else if (Array.isArray(v)) {
      result[k] = v.map((item) =>
        item && typeof item === 'object' && !Array.isArray(item)
          ? sanitize(item as Record<string, unknown>)
          : item
      );
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      result[k] = sanitize(v as Record<string, unknown>);
    } else {
      result[k] = v;
    }
  }
  return result;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const masked = local.slice(0, 2) + '**';
  const [host, ...tld] = domain.split('.');
  return `${masked}@${host.slice(0, 2)}***${tld.length ? '.' + tld.join('.') : ''}`;
}

function decodeJwtExp(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    if (typeof decoded.exp === 'number') {
      return new Date(decoded.exp * 1000).toISOString();
    }
    return null;
  } catch {
    return null;
  }
}

export interface DebugReport {
  meta: {
    generatedAt: string;
    appVersion: string;
    environment: string;
    sessionId: string | null;
  };
  auth: {
    isAuthenticated: boolean;
    emailPartial: string | null;
    role: string | null;
    tokenExpiresAt: string | null;
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
  };
  form: {
    activeForm: 'tutor' | 'parent' | 'music_tutor' | 'music_parent' | null;
    currentStep: number | null;
    stepCount: number | null;
    formDataSanitized: Record<string, unknown> | null;
    validationErrors: Record<string, string> | null;
    isSnapshot: boolean | null;
  };
  page: {
    name: string | null;
    stateSanitized: Record<string, unknown> | null;
  };
  device: {
    userAgent: string;
    viewport: { width: number; height: number };
    language: string;
    connectionType?: string;
    deviceMemoryGB?: number;
    cores: number | null;
    touchSupport: boolean;
    platform: string;
  };
  navigation: {
    currentUrl: string;
    pathname: string;
    search: string;
  };
  localStorage: {
    keys: string[];
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    hasBackendUser: boolean;
    hasUserProfile: boolean;
    userRole: string | null;
  };
  reactQuery: {
    queriesCount: number;
    staleCount: number;
    fetchingCount: number;
    queries: Array<{ queryKey: string; status: string; isStale: boolean }>;
  };
  profile: {
    profileIdPresent: boolean;
    backendUserIdPresent: boolean;
    role: string | null;
    verificationStatus: string | null;
    hasVerificationDocuments: boolean;
    verificationDocumentCount: number | null;
  };
  console: typeof debugStore.consoleEntries;
  network: typeof debugStore.networkEntries;
  rejections: string[];
  breadcrumbs: typeof debugStore.breadcrumbs;
}

export function buildDebugReport(auth: AuthContextType, queryClient: QueryClient): DebugReport {
  // Auth
  const accessToken = localStorage.getItem('client_accessToken');
  const refreshToken = localStorage.getItem('client_refreshToken');
  const email = auth.firebaseUser?.email ?? auth.backendUser?.email ?? null;

  // Form
  const formDebug = window.__debugBridge?.getFormDebugData() ?? null;
  const pageDebug = window.__debugBridge?.getPageDebugData() ?? null;

  // localStorage
  const localStorageKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) localStorageKeys.push(key);
  }

  // React Query
  const allQueries = queryClient.getQueryCache().getAll();
  const querySnapshots = allQueries.map((q) => ({
    queryKey: JSON.stringify(q.queryKey),
    status: q.state.status,
    isStale: q.isStale(),
  }));

  // Navigator connection (non-standard API)
  const nav = navigator as Navigator & {
    connection?: { effectiveType?: string };
    deviceMemory?: number;
    hardwareConcurrency?: number;
  };

  return {
    meta: {
      generatedAt: new Date().toISOString(),
      appVersion: import.meta.env.VITE_RELEASE ?? 'unknown',
      environment: import.meta.env.MODE,
      sessionId: sessionStorage.getItem('x-session-id'),
    },
    auth: {
      isAuthenticated: !!auth.firebaseUser,
      emailPartial: email ? maskEmail(email) : null,
      role: auth.backendUser?.currentRole ?? null,
      tokenExpiresAt: accessToken ? decodeJwtExp(accessToken) : null,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
    },
    form: formDebug
      ? {
          activeForm: formDebug.type,
          currentStep: formDebug.data.currentStep,
          stepCount: formDebug.data.stepCount,
          formDataSanitized: sanitize(formDebug.data.formData),
          validationErrors: formDebug.data.validationErrors,
          isSnapshot: formDebug.isSnapshot,
        }
      : {
          activeForm: null,
          currentStep: null,
          stepCount: null,
          formDataSanitized: null,
          validationErrors: null,
          isSnapshot: null,
        },
    page: pageDebug
      ? {
          name: pageDebug.page,
          stateSanitized: sanitize(pageDebug.state),
        }
      : {
          name: null,
          stateSanitized: null,
        },
    device: {
      userAgent: navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      language: navigator.language,
      connectionType: nav.connection?.effectiveType ?? undefined,
      deviceMemoryGB: nav.deviceMemory ?? undefined,
      cores: nav.hardwareConcurrency ?? null,
      touchSupport: 'ontouchstart' in window,
      platform: navigator.platform,
    },
    navigation: {
      currentUrl: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
    },
    localStorage: {
      keys: localStorageKeys,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasBackendUser: !!localStorage.getItem('client_backendUser'),
      hasUserProfile: !!localStorage.getItem('client_userProfile'),
      userRole: localStorage.getItem('client_userRole'),
    },
    reactQuery: {
      queriesCount: allQueries.length,
      staleCount: querySnapshots.filter((q) => q.isStale).length,
      fetchingCount: querySnapshots.filter((q) => q.status === 'pending').length,
      queries: querySnapshots,
    },
    profile: {
      profileIdPresent: !!auth.profile?.id,
      backendUserIdPresent: !!auth.backendUser?.userId,
      role: auth.profile?.role ?? auth.backendUser?.currentRole ?? null,
      verificationStatus: auth.profile?.verificationStatus ?? null,
      hasVerificationDocuments: !!auth.profile?.verificationDocuments?.length,
      verificationDocumentCount: auth.profile?.verificationDocuments?.length ?? null,
    },
    console: [...debugStore.consoleEntries],
    network: [...debugStore.networkEntries],
    rejections: [...debugStore.rejections],
    breadcrumbs: [...debugStore.breadcrumbs],
  };
}
